import { Ref, useImperativeHandle, useMemo } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm } from 'react-hook-form';

import { usePathCountryCode } from '../../../../../../../hooks/use-path-locale';
import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqTimeframeOptions } from '../fields/rfq-timeframe-options';

export interface FormValue {
   country: DropdownOption;
   city: string;
   postalCode: string;
   timeFrame: DropdownOption;
}

export interface LocalFormValue {
   country: DropdownOption | undefined;
   city: string;
   postalCode: string;
   timeFrame: DropdownOption | undefined;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   countries: { fullName: string; shortName: string }[] | undefined;
   defaultFormValue: FormValue | undefined;
   onSubmit: (value: FormValue) => void;
}

export const RfqFlyoutStepDeliveryArea = ({ stepRef, countries, defaultFormValue, onSubmit }: Props) => {
   const translate = useTranslations();
   const countryCode = usePathCountryCode();

   const countryOptions: DropdownOption[] = useMemo(
      () =>
         (countries ?? [])
            .map((country) => ({
               id: country.shortName,
               value: country.fullName
            }))
            .sort((a, b) => a.value.localeCompare(b.value)),
      [countries]
   );

   const { control, handleSubmit } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ?? {
         country: countryOptions.find((countryOption) => countryOption.id === countryCode),
         city: '',
         postalCode: '',
         timeFrame: undefined
      }
   });

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   return (
      <Box direction="column">
         <Box direction="column" gap="md">
            <Typography component="span" variant="body-highlight">
               {translate('page-consumer.request-for-quotation.delivery-area')}
            </Typography>
            <Typography component="span" variant="body">
               {translate('page-consumer.request-for-quotation.delivery-area-description')}
            </Typography>
            <Controller
               control={control}
               name="country"
               rules={{
                  required: translate('form-validation.required', { field: translate('page-consumer.request-for-quotation.contact-info.country') })
               }}
               render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <Dropdown
                     inputRef={ref}
                     {...field}
                     id="rfq-delivery-area-country"
                     label={translate('page-consumer.request-for-quotation.contact-info.country')}
                     emptyOption={false}
                     disabled={!countries}
                     options={countryOptions}
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />

            <Box gap="xs">
               <Controller
                  control={control}
                  name="city"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.city') }),
                     maxLength: {
                        value: 40,
                        message: translate('form-validation.max-length', { field: translate('form-field.city'), value: 40 })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        label={translate('page-consumer.request-for-quotation.city')}
                        id="rfq-delivery-area-city"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
               <Controller
                  control={control}
                  name="postalCode"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.zip-postal-code') }),
                     maxLength: {
                        value: 10,
                        message: translate('form-validation.max-length', {
                           field: translate('form-field.zip-postal-code'),
                           value: 10
                        })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        label={translate('page-consumer.request-for-quotation.zip-postal-code')}
                        id="rfq-delivery-area-zip"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </Box>
         </Box>
         <RfqTimeframeOptions control={control} name="timeFrame" />
      </Box>
   );
};
